<template>
  <div>
    <v-row>
      <v-col
        cols="12"
        sm="12"
        md="4"
      >
        <VAutocomplete
          ref="RefNetworkType"
          v-model="data.networkType"
          :label="$t('forms.labels.networkType')"
          :items="displayedNetworkTypes"
          itemText="name"
          itemValue="id"
          :loading="loading"
          :validateAtInitialization="!!data.id"
          outlined
          @change="updateData"
        >
          <template #withInfiniteScroll>
            <div v-intersect="(_, __, isIntersecting) => endIntersect(isIntersecting, 'networkTypes')" />
          </template>
        </VAutocomplete>
      </v-col>
      <v-col
        cols="12"
        sm="12"
        md="4"
      >
        <VAutocomplete
          ref="RefComponentType"
          v-model="data.componentType"
          :label="$t('forms.labels.componentType')"
          :items="displayedComponentTypes"
          itemText="name"
          itemValue="id"
          :loading="loading"
          :validateAtInitialization="!!data.id"
          outlined
          @change="updateData"
        >
          <template #withInfiniteScroll>
            <div v-intersect="(_, __, isIntersecting) => endIntersect(isIntersecting, 'componentTypes')" />
          </template>
        </VAutocomplete>
      </v-col>
      <v-col
        cols="12"
        sm="12"
        md="4"
      >
        <VAutocomplete
          v-model="data.analysisType"
          :label="$t('forms.labels.analysisType')"
          :items="displayedAnalysisTypes"
          outlined
          :loading="loading"
          :rules="{ required: true }"
          :disabled="!!data.id"
          :validateAtInitialization="!!data.id"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col
        cols="12"
        sm="12"
        md="4"
      >
        <VTextField
          v-model="data.norm.normal"
          :label="$t('sites.norms.normal')"
          outlined
          color="#4CAF50"
          readonly
          :rules="{ bNorms: [data.norm.intervention, data.norm.risk]}"
        />
      </v-col>

      <v-col
        cols="12"
        sm="12"
        md="4"
      >
        <VTextField
          v-model="data.norm.intervention"
          outlined
          :formatNumber="true"

          :label="$t('sites.norms.intervention')"
          color="#FDD835"
          :rules="{ required: true, sNorms: [data.norm.normal], bNorms: [data.norm.risk]}"
        />
      </v-col>
      <v-col
        cols="12"
        sm="12"
        md="4"
      >
        <VTextField
          v-model="data.norm.risk"
          :formatNumber="true"
          outlined
          :label="$t('sites.norms.risk')"
          color="#F44336"
          :rules="{ required: true, sNorms: [data.norm.normal, data.norm.intervention]}"
        />
      </v-col>
    </v-row>

    <v-row
      v-for="(note, index) in notes"
      :key="index"
      align="center"
      justify="center"
      class="action"
    >
      <v-col
        alignSelf="start"
        cols="12"
        sm="12"
        md="2"
      >
        <VSelect
          v-model="note.lang"
          :label="$t('forms.labels.lang')"
          itemValue="key"
          outlined
          :items="langs"
        />
      </v-col>

      <v-col>
        <VTextarea
          v-model="note.normal"
          :label="$t('sites.norms.normalAction')"
          outlined
          :rules="{ max: 600 }"
        />
      </v-col>

      <v-col>
        <VTextarea
          v-model="note.intervention"
          :label="$t('sites.norms.interventionAction')"
          outlined
          :rules="{ max: 600 }"
        />
      </v-col>

      <v-col>
        <VTextarea
          v-model="note.risk"
          :label="$t('sites.norms.riskAction')"
          outlined
          :rules="{ max: 600 }"
        />
      </v-col>
      <div>
        <v-btn
          icon
          :disabled="langs.length === notes.length"
          @click="onClickAddRow"
        >
          <v-icon>
            add
          </v-icon>
        </v-btn>
        <v-btn
          icon
          :disabled="notes.length === 1"
          @click="onClickRemoveRow(index)"
        >
          <v-icon>
            close
          </v-icon>
        </v-btn>
      </div>
    </v-row>
  </div>
</template>

<script>
import { LOCALES } from '@/locales/i18n';
import { cloneDeep } from 'lodash';
import { mapGetters } from 'vuex';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import VTextarea from '@/components/shared/widgets/forms/VuetifyInputs/VTextarea.vue';
import analysisTypesConstant from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';

export default {
    components: {
        VTextField,
        VTextarea,
        VSelect,
        VAutocomplete,
    },
    props: {
        standard: {
            type: Object,
            default: () => ({}),
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
        standards: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            // Backend takes null for 'any'
            networkTypes: [
                {
                    id: null,
                    name: this.$t('forms.labels.any'),
                },
            ],
            componentTypes: [
                {
                    id: null,
                    name: this.$t('forms.labels.any'),
                },
            ],
            analysisTypes: [],
            data: {
                networkType: null,
                componentType: null,
                analysisType: null,
                norm: {
                    notes: {
                        normal: {},
                        intervention: {},
                        risk: {},
                    },
                    normal: '0',
                    intervention: null,
                    risk: null,
                },
            },
            loading: false,
            notes: [
                {
                    lang: '',
                    normal: '',
                    intervention: '',
                    risk: '',
                },
            ],
            networksTypesPagination: {},
            componentTypesPagination: {},
            networksTypesQueryParams: { page: 1, perpage: 25, search: '' },
            componentTypesQueryParams: { page: 1, perpage: 25, search: '' },
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'teamMainLang',
        ]),
        langs() {
            return LOCALES.map(lang => ({
                name: this.$t(`langs.${lang}`),
                key: lang,
                disabled: this.isLangUsed(lang),
            }));
        },
        isLangUsed() {
            return lang => this.notes.find(note => note.lang === lang);
        },
        displayedAnalysisTypes() {
            // For now the category for both languages would be the same
            return this.analysisTypes.map(({ id, name, measureUnit }) => {
                let displayName = name[this.userLang] || name[this.teamMainLang];
                if (measureUnit && (measureUnit[this.userLang] || measureUnit[this.teamMainLang])) {
                    displayName += `(${measureUnit[this.userLang] || measureUnit[this.teamMainLang] })`;
                }
                return {
                    id,
                    name: displayName,
                    disabled: this.isAnalysisTypeUsed(id),

                };
            });
        },
        displayedComponentTypes() {
            return this.getDisplayedItem(this.componentTypes);
        },
        displayedNetworkTypes() {
            return this.getDisplayedItem(this.networkTypes);
        },
    },
    watch: {
        data: {
            handler() {
                this.$emit('sendData', this.data);
            },
            deep: true,
        },
        notes: {
            handler() {
                this.notes.forEach(note => {
                    if (note.lang) {
                        Object.keys(note).forEach(action => {
                            if (action !== 'lang') {
                                this.data.norm.notes[action][note.lang] = note[action];
                            }
                        });
                    }
                });
            },
            deep: true,
        },
    },
    async created() {
        if (this.isEdition) {
            this.data = this.prepareStandard();
            this.prepareNotes();
        }
        await this.getData();
    },
    methods: {
        prepareStandard() {
            const result = cloneDeep(this.standard);
            result.norm.intervention = result.norm.intervention.toString();
            result.norm.risk = result.norm.risk.toString();
            result.norm.normal = '0';
            return result;
        },
        prepareNotes() {
            if (!this.data.norm.notes) {
                this.data.norm.notes = {
                    normal: {},
                    intervention: {},
                    risk: {},
                };
                return;
            }

            Object.keys(this.data.norm.notes).forEach(note => {
                Object.keys(this.data.norm.notes[note]).forEach((lang, index) => {
                    if (this.notes.length === index) {
                        this.onClickAddRow();
                    }

                    this.notes[index].lang = lang;
                    this.notes[index][note] = this.data.norm.notes[note][lang];
                });
            });
        },
        async getData() {
            this.loading = true;
            await this.getNetworkTypes();
            await this.getComponentTypes();
            await this.getAnalysisTypes(analysisTypesConstant.TYPE_LEGIONELLA);
            this.loading = false;
        },
        async getAnalysisTypes(typeName) {
            const { data } = await this.$rq.getAnalysisTypes({ typeName, pagination: false });
            this.analysisTypes = data.types;
        },
        hasType(standardType, dataType) {
            return standardType ? dataType === standardType.id : dataType === null;
        },
        isAnalysisTypeUsed(id) {
            const existingStandard = this.standards.filter(standard => {
                const { componentType, networkType } = standard;
                return this.hasType(componentType, this.data.componentType)
                && this.hasType(networkType, this.data.networkType);
            });
            const selectedAnalysisTypes = [];
            existingStandard.forEach(standard => { if (standard.analysisType) selectedAnalysisTypes.push(standard.analysisType.id); });

            return selectedAnalysisTypes.includes(id);
        },
        onClickAddRow() {
            this.notes.push({
                lang: '',
                normal: '',
                intervention: '',
                risk: '',
            });
        },
        onClickRemoveRow(index) {
            this.notes.splice(index, 1);
        },
        async getNetworkTypes() {
            if (this.isEdition) {
                const { data } = await this.$rq.getNetworkTypes({ pagination: false });
                this.networkTypes = data.networkTypes;
            } else if (this.networksTypesPagination?.currentPage !== this.networksTypesQueryParams?.page
            && this.networksTypesPagination?.totalPageCount
                ? this.networksTypesPagination?.totalPageCount >= this.networksTypesQueryParams?.page : true) {
                const { data } = await this.$rq.getNetworkTypes(this.networksTypesQueryParams);
                this.networkTypes = [...data.networkTypes, ...this.networkTypes];
                this.networksTypesPagination = data.pagination;
            }
        },
        async getComponentTypes() {
            if (this.isEdition) {
                const { data } = await this.$rq.getComponentTypes({ pagination: false });
                this.componentTypes = data.componentTypes;
            } else if (this.componentTypesPagination?.currentPage !== this.componentTypesQueryParams?.page
            && this.componentTypesPagination?.totalPageCount
                ? this.componentTypesPagination?.totalPageCount >= this.componentTypesQueryParams?.page : true) {
                const { data } = await this.$rq.getComponentTypes(this.componentTypesQueryParams);
                this.componentTypes = [...data.componentTypes, ...this.componentTypes];
                this.componentTypesPagination = data.pagination;
            }
        },
        getDisplayedItem(item) {
            return item?.map(({ id, name }) => ({
                id,
                name: id ? name[this.selectedLang] || name[this.teamMainLang] : name,
            }));
        },
        async endIntersectComponentTypes(isIntersecting) {
            const condition = this.componentTypesPagination?.currentPage < this.componentTypesPagination?.totalPageCount;
            if (isIntersecting && condition) {
                this.componentTypesQueryParams.page = this.componentTypesPagination.currentPage + 1;
                await this.getComponentTypes();
                const ref = this.$refs?.refComponentTypes?.$refs?.autocomplete;
                if (ref?.onScroll) {
                    // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async endIntersectNetworkTypes(isIntersecting) {
            const condition = this.networksTypesPagination?.currentPage < this.networksTypesPagination?.totalPageCount;
            if (isIntersecting && condition) {
                this.networksTypesQueryParams.page = this.networksTypesPagination.currentPage + 1;
                await this.getNetworkTypes();
                const ref = this.$refs?.refNetworkTypes?.$refs?.autocomplete;
                if (ref?.onScroll) {
                    // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async endIntersect(isIntersecting, field) {
            switch (field) {
                case 'componentTypes':
                    await this.endIntersectComponentTypes(isIntersecting);
                    break;
                case 'networkTypes':
                    await this.endIntersectNetworkTypes(isIntersecting);
                    break;
                case 'coupons':
                    await this.endIntersectCoupons(isIntersecting);
                    break;
                default:
            }
        },
    },
};
</script>

<style lang="scss" scoped>
  @media screen and (max-width: 960px) {
    .action {
      display: flex !important;
      flex-direction: column !important;
      border-bottom: 1px solid #A3A3A3;
      margin-bottom: 20px
    }
  }
</style>
